import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import { Form as FormAntd, Table } from 'antd';

import {
  ColSize,
  ContentSection,
  RowSize,
  SwitchForm,
} from '../../../../../../../../../../../shared/components';

import { StepTwoFormContext } from '../../../../contexts/StepTwoForm';
import { QuotationInfoContext } from '../../../../../../../shared';

import './index.css';
import { Feature } from '../../../../../../../../../../../shared/interfaces';
import { fixEncoding } from '../../../../../../../../../../../shared/services/insurancePolicy/utils';

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Item',
    dataIndex: 'codeItem',
    width: '10%',
  },
  {
    title: 'Código',
    dataIndex: 'featureCode',
    width: '10%',
  },
  {
    title: 'Descripción de Características',
    dataIndex: 'featureDescription',
    width: '70%',
  },
  {
    title: '¿Tiene?',
    dataIndex: 'hasItem',
    width: '10%',
  },
];

interface TableFeaturesProps {
  duplicateId?: number;
  id: number;
}

export const TableFeatures: FC<TableFeaturesProps> = ({ duplicateId, id }) => {
  const { stepTwoForm, allFeatures, setAllFeatures, getFeaturesRisks } = useContext(StepTwoFormContext);
  const { quotationToRenewal, savedQuotation } = useContext(QuotationInfoContext);
  const quotation = quotationToRenewal ?? savedQuotation;
  const isRenewal = !!quotationToRenewal;

  const planFormItemName = `plan-tab-${id}`;
  const planSelected = FormAntd.useWatch(planFormItemName, stepTwoForm);

  const getFeaturesRiskByPlan = useCallback(
    async (planSelected: string) => {
      const isFeatureAlreadyAdded = allFeatures.some(
        (feature: any) => feature.plan === planSelected
      );

      if (isFeatureAlreadyAdded) return;

      try {
        const bien = quotation?.bienes?.[id - 1];
        const componente = Number(bien?.numeroComponente ?? id);

        const featuresList = await getFeaturesRisks(planSelected, componente);

        setAllFeatures((prevValues) => [
          ...prevValues,
          { plan: planSelected, keyTab: id, features: featuresList },
        ]);
      } catch (error) {} 
      finally {}
    },
    [setAllFeatures, getFeaturesRisks, id, allFeatures, quotation]
  );

  const setFeaturesTable = useMemo(
    () =>
      allFeatures?.find((feature) => feature.plan === planSelected)?.features.length
        ? allFeatures
            ?.find((feature) => feature.plan === planSelected)
            ?.features?.map((feature: Feature) => ({
              codeItem: feature.codigo,
              featureCode: feature.codigoEquivalente,
              featureDescription: fixEncoding(feature.descripcion),
              hasItem: (
                <SwitchForm
                  formProps={{
                    initialValue: feature.valorPorDefecto,
                    name: `feature-tab-${id}-${feature.codigo}`,
                  }}
                  switchProps={{ disabled: isRenewal || !feature.permiteModificar }}
                />
              ),
              key: feature.codigo,
            }))
        : [],
    [allFeatures, planSelected, id]
  );

  useEffect(() => {
    if (duplicateId) {
      const allFieldsValues = stepTwoForm?.getFieldsValue(true);

      const duplicatedFieldsValues = Object.keys(allFieldsValues).reduce(
        (acc, key) => {
          if (key.includes(`feature-tab-${duplicateId}`)) {
            const newKey = key.replace(
              `feature-tab-${duplicateId}-`,
              `feature-tab-${id}-`
            );
            acc[newKey] = allFieldsValues[key];
          }

          return acc;
        },
        {} as Record<string, any>
      );

      stepTwoForm?.setFieldsValue(duplicatedFieldsValues);
    }
  }, [duplicateId, id, stepTwoForm]);

  useEffect(() => {
    if (planSelected) getFeaturesRiskByPlan(planSelected);
  }, [planSelected, getFeaturesRiskByPlan, planSelected]);

  return (
    <ContentSection level='h3' title='Características' hasCard={false}>
      <RowSize rowProps={{ gutter: [0, 0] }}>
        <ColSize size={100}>
          <Table
            bordered
            columns={MOCK_TABLE_COLUMNS_HEADER}
            dataSource={setFeaturesTable}
            pagination={false}
            className='person-table'
            scroll={{ x: 'auto' }}
          />
        </ColSize>
      </RowSize>
    </ContentSection>
  );
};
